import React, { useState, useEffect } from 'react';
import { useCollegePrepContext } from '../../../contexts/CollegePrepContext';

const PersonalTimeline = () => {
  const { programs, updateTimelineAction } = useCollegePrepContext();
  const [currentPhase, setCurrentPhase] = useState(null);

  const getCurrentPhase = () => {
    const now = new Date();
    const currentMonth = now.getMonth(); // 0-11
    const currentYear = now.getFullYear();
    
    // October 2024 - November 2024: Research & Discovery
    if ((currentYear === 2024 && currentMonth >= 9 && currentMonth <= 10)) {
      return 'research';
    }
    // December 2024 - January 2025: Application Preparation
    else if ((currentYear === 2024 && currentMonth === 11) || (currentYear === 2025 && currentMonth === 0)) {
      return 'prep';
    }
    // February 2025 - April 2025: Application Season
    else if (currentYear === 2025 && currentMonth >= 1 && currentMonth <= 3) {
      return 'applications';
    }
    // May 2025 - June 2025: Decision Time
    else if (currentYear === 2025 && currentMonth >= 4 && currentMonth <= 5) {
      return 'decisions';
    }
    // Default to research phase
    else {
      return 'research';
    }
  };

  useEffect(() => {
    setCurrentPhase(getCurrentPhase());
  }, []);

  const timelinePhases = {
    research: {
      id: 'research',
      title: '📚 Deep Research Sprint',
      timeframe: 'NOW - Nov 30',
      description: 'Perfect discovery window with 6+ months to plan',
      isActive: true,
      actions: [
        {
          id: 'nc-governors-research',
          description: 'Research NC Governor\'s School tracks + requirements',
          completed: false,
          relatedPrograms: programs.filter(p => p.name.toLowerCase().includes('governor')).map(p => p.name)
        },
        {
          id: 'local-companies',
          description: 'Find 2 local RTP biotech/AI companies with internship programs',
          completed: false,
          relatedPrograms: []
        },
        {
          id: 'rsi-connections',
          description: 'Connect with 1 person who did RSI or similar competitive program',
          completed: false,
          relatedPrograms: programs.filter(p => p.name.toLowerCase().includes('rsi')).map(p => p.name)
        },
        {
          id: 'interest-exploration',
          description: 'Complete interest exploration',
          completed: true,
          relatedPrograms: []
        }
      ],
      smartAlerts: [
        'NC Governor\'s School app opens Dec 1 - research NOW while you have time',
        'RSI essays are notoriously difficult - start brainstorming early'
      ]
    },
    prep: {
      id: 'prep',
      title: '✍️ Application Prep',
      timeframe: 'Dec 1 - Jan 31',
      description: 'Get materials ready for application season',
      isActive: false,
      actions: [
        {
          id: 'draft-essays',
          description: 'Draft essays for top 3 programs',
          completed: false,
          relatedPrograms: programs.slice(0, 3).map(p => p.name)
        },
        {
          id: 'recommendation-letters',
          description: 'Request recommendation letters (give 3+ weeks notice)',
          completed: false,
          relatedPrograms: []
        },
        {
          id: 'transcript-request',
          description: 'Get transcript request submitted',
          completed: false,
          relatedPrograms: []
        }
      ],
      smartAlerts: [
        'Start essays early - they take multiple drafts',
        'Give teachers plenty of notice for recommendation letters'
      ]
    },
    applications: {
      id: 'applications',
      title: '�� Application Season',
      timeframe: 'Feb 1 - Apr 30',
      description: 'Submit applications strategically',
      isActive: false,
      actions: [
        {
          id: 'early-deadlines',
          description: 'Submit early deadline applications (RSI, TASP)',
          completed: false,
          relatedPrograms: programs.filter(p => 
            p.name.toLowerCase().includes('rsi') || 
            p.name.toLowerCase().includes('tasp')
          ).map(p => p.name)
        },
        {
          id: 'main-season',
          description: 'Submit main season applications',
          completed: false,
          relatedPrograms: programs.filter(p => !p.name.toLowerCase().includes('rsi')).map(p => p.name)
        },
        {
          id: 'backup-programs',
          description: 'Apply to backup/rolling programs',
          completed: false,
          relatedPrograms: []
        }
      ],
      smartAlerts: [
        'Submit applications in order of preference',
        'Keep track of confirmation emails'
      ]
    },
    decisions: {
      id: 'decisions',
      title: '🎉 Decision Time',
      timeframe: 'May 1 - Jun 1',
      description: 'Make final choices and prepare',
      isActive: false,
      actions: [
        {
          id: 'review-acceptances',
          description: 'Review acceptances and make final choice',
          completed: false,
          relatedPrograms: []
        },
        {
          id: 'enrollment',
          description: 'Complete enrollment/confirmation process',
          completed: false,
          relatedPrograms: []
        },
        {
          id: 'logistics',
          description: 'Plan logistics (travel, housing, prep)',
          completed: false,
          relatedPrograms: []
        }
      ],
      smartAlerts: [
        'Accept/decline by deadlines',
        'Start planning summer logistics early'
      ]
    }
  };

  const handleActionToggle = (phaseId, actionId) => {
    const phase = timelinePhases[phaseId];
    const action = phase.actions.find(a => a.id === actionId);
    
    if (action) {
      // Update the action in the timeline
      const updatedPhases = { ...timelinePhases };
      updatedPhases[phaseId].actions = updatedPhases[phaseId].actions.map(a => 
        a.id === actionId ? { ...a, completed: !a.completed } : a
      );
      
      // Also update in context for persistence
      updateTimelineAction(actionId, { completed: !action.completed });
    }
  };

  const getPhaseProgress = (phase) => {
    const completedActions = phase.actions.filter(a => a.completed).length;
    return Math.round((completedActions / phase.actions.length) * 100);
  };

  const getCurrentPhaseInfo = () => {
    return timelinePhases[currentPhase];
  };

  const getPhasesByOrder = () => {
    return ['research', 'prep', 'applications', 'decisions'].map(key => timelinePhases[key]);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Current Phase Highlight */}
      {currentPhase && (
        <div className="bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl p-6 mb-8 text-center">
          <h2 className="text-2xl font-bold mb-2">
            🎯 {getCurrentPhaseInfo().title.replace(/📚|✍️|🎯|🎉/, '').trim()}
          </h2>
          <p className="text-amber-100">
            {getCurrentPhaseInfo().timeframe} - {getCurrentPhaseInfo().description}
          </p>
        </div>
      )}

      {/* Personal Timeline */}
      <div className="space-y-6">
        {getPhasesByOrder().map((phase) => {
          const isCurrentPhase = phase.id === currentPhase;
          const progress = getPhaseProgress(phase);
          
          return (
            <div 
              key={phase.id} 
              className={`border-2 rounded-xl overflow-hidden transition-all ${
                isCurrentPhase 
                  ? 'border-amber-300 shadow-lg bg-amber-50' 
                  : 'border-stone-200 bg-white'
              }`}
            >
              {/* Phase Header */}
              <div className={`p-6 ${
                isCurrentPhase ? 'bg-amber-100' : 'bg-stone-50'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className={`text-xl font-semibold ${
                      isCurrentPhase ? 'text-amber-800' : 'text-slate-900'
                    }`}>
                      {phase.title}
                    </h3>
                    <p className={`text-sm ${
                      isCurrentPhase ? 'text-amber-600' : 'text-stone-600'
                    }`}>
                      {phase.timeframe}
                    </p>
                  </div>
                  
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${
                      isCurrentPhase ? 'text-amber-700' : 'text-stone-700'
                    }`}>
                      {progress}%
                    </div>
                    <div className="w-24 bg-stone-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all ${
                          isCurrentPhase ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Phase Content */}
              <div className="p-6">
                {/* Next Actions */}
                <div className="mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">
                    🎯 {isCurrentPhase ? 'Your Next Actions (This Month)' : 'Planned Actions'}
                  </h4>
                  <div className="space-y-3">
                    {phase.actions.map((action) => (
                      <div 
                        key={action.id}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-stone-50 transition-colors"
                      >
                        <button
                          onClick={() => handleActionToggle(phase.id, action.id)}
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 transition-all ${
                            action.completed
                              ? 'bg-emerald-500 border-emerald-500 text-white'
                              : 'border-stone-300 hover:border-emerald-300'
                          }`}
                        >
                          {action.completed && '✓'}
                        </button>
                        
                        <div className="flex-1">
                          <span className={`text-sm ${
                            action.completed 
                              ? 'line-through text-stone-500' 
                              : 'text-slate-900'
                          }`}>
                            {action.description}
                          </span>
                          
                          {action.relatedPrograms.length > 0 && (
                            <div className="mt-1">
                              <div className="text-xs text-stone-500 mb-1">Related programs:</div>
                              <div className="flex flex-wrap gap-1">
                                {action.relatedPrograms.slice(0, 3).map((program, idx) => (
                                  <span 
                                    key={idx}
                                    className="px-2 py-1 bg-azure-100 text-azure-700 text-xs rounded-full"
                                  >
                                    {program}
                                  </span>
                                ))}
                                {action.relatedPrograms.length > 3 && (
                                  <span className="text-xs text-stone-500">
                                    +{action.relatedPrograms.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Smart Alerts */}
                {phase.smartAlerts.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">⚡</span>
                      <h5 className="font-semibold text-yellow-800">Smart Reminders</h5>
                    </div>
                    <div className="space-y-1">
                      {phase.smartAlerts.map((alert, index) => (
                        <div key={index} className="text-sm text-yellow-700">
                          • {alert}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PersonalTimeline;